// Concrete SyncBackend over Supabase Postgres + Storage — the server end of the
// phase-1 sync seam. `push` upserts a collection's queued writes/tombstones;
// `pull` (the phase-3 merge) reads everything changed since a timestamp. Row
// access is scoped to the signed-in user by RLS, and every row is stamped with
// `owner_id` so the insert policy accepts it.
//
// Phase 4: heavy blobs don't live in the row. On push the thumbnail PNG is
// uploaded to the private `thumbnails` bucket and the row keeps only its path;
// the reference photo is dropped entirely (device-local aid, never uploaded). On
// pull the thumbnail is downloaded and re-inlined so the local record (and the
// collection dock) work offline exactly as before. Reference is absent from
// pulled payloads — the controller preserves the local copy on merge.

import type { SupabaseClient } from "@supabase/supabase-js";
import type { SyncBackend, SyncRecord } from "./backend";
import {
  MAX_THUMBNAIL_BYTES,
  PermanentSyncError,
  STRIPPED_BLOB_FIELDS,
  isPermanentPushErrorCode,
} from "./limits";

const BUCKET = "thumbnails";

// A stored row mirrors the migration's columns; `data` is the saved doc MINUS
// the blobs, `thumb_path` points at the Storage object.
interface Row {
  id: string;
  owner_id: string;
  name: string;
  data: unknown | null;
  thumb_path: string | null;
  deleted: boolean;
  created_at: number;
  updated_at: number;
}

type PulledRow = Pick<Row, "id" | "data" | "thumb_path" | "deleted" | "updated_at">;

function nameOf(payload: unknown): string {
  const n = (payload as { name?: unknown } | null)?.name;
  return typeof n === "string" ? n : "";
}
function createdOf(payload: unknown, fallback: number): number {
  const c = (payload as { createdAt?: unknown } | null)?.createdAt;
  return typeof c === "number" ? c : fallback;
}

// Split a saved doc into the lightweight row payload + the thumbnail to upload.
// Drops `thumbnail` (→ Storage) and `reference` (device-local, never synced) —
// the field list is shared with limits.ts so the controller's pre-push size
// check measures exactly what gets sent.
function stripBlobs(payload: unknown): { row: Record<string, unknown>; thumb: string | null } {
  const p = { ...(payload as Record<string, unknown>) };
  const t = p.thumbnail;
  const thumb = typeof t === "string" && t.startsWith("data:") ? t : null;
  for (const f of STRIPPED_BLOB_FIELDS) delete p[f]; // reference: models only; layouts have none
  return { row: p, thumb };
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

export class SupabaseBackend implements SyncBackend {
  constructor(
    private readonly client: SupabaseClient,
    private readonly ownerId: string,
  ) {}

  private thumbPath(table: string, id: string): string {
    return `${this.ownerId}/${table}/${id}.png`;
  }

  private async uploadThumb(table: string, id: string, dataUrl: string): Promise<string> {
    const blob = await (await fetch(dataUrl)).blob();
    // The bucket enforces this cap server-side (0003_limits.sql); checking here
    // skips a doomed round-trip. The thrown error lands in push's catch, which
    // falls back to embedding the thumbnail inline — bounded by the row cap.
    if (blob.size > MAX_THUMBNAIL_BYTES)
      throw new Error(`thumbnail is ${blob.size} bytes (cap ${MAX_THUMBNAIL_BYTES})`);
    const path = this.thumbPath(table, id);
    const { error } = await this.client.storage
      .from(BUCKET)
      .upload(path, blob, { upsert: true, contentType: "image/png" });
    if (error) throw new Error(`thumbnail upload failed: ${error.message}`);
    return path;
  }

  private async downloadThumb(path: string): Promise<string | null> {
    const { data, error } = await this.client.storage.from(BUCKET).download(path);
    if (error || !data) return null;
    return blobToDataUrl(data);
  }

  async push(table: string, records: SyncRecord[]): Promise<void> {
    if (records.length === 0) return;
    const rows: Row[] = [];
    for (const r of records) {
      if (r.deleted) {
        // Best-effort cleanup of the orphaned thumbnail; the tombstone row is
        // what actually propagates the delete.
        await this.client.storage.from(BUCKET).remove([this.thumbPath(table, r.id)]).catch(() => {});
        rows.push({
          id: r.id, owner_id: this.ownerId, name: "", data: null, thumb_path: null,
          deleted: true, created_at: r.updatedAt, updated_at: r.updatedAt,
        });
        continue;
      }
      const { row, thumb } = stripBlobs(r.payload);
      let thumb_path: string | null = null;
      if (thumb) {
        try {
          thumb_path = await this.uploadThumb(table, r.id, thumb);
        } catch (e) {
          // Non-fatal: keep the thumbnail INLINE in the row so the model still
          // round-trips (heavier, but never silently lost). Surface why so a
          // bucket/policy problem is diagnosable instead of invisible.
          console.warn(`[sync] thumbnail upload failed for ${table}/${r.id}; embedding inline instead`, e);
          row.thumbnail = thumb;
        }
      }
      rows.push({
        id: r.id, owner_id: this.ownerId, name: nameOf(r.payload), data: row, thumb_path,
        deleted: false, created_at: createdOf(r.payload, r.updatedAt), updated_at: r.updatedAt,
      });
    }
    // The server's lww_guard trigger drops any row whose updated_at is older
    // than what's stored. A TRANSIENT error (network, auth) leaves the outbox
    // entries queued for retry; an error Postgres classifies as a fault in the
    // request itself (e.g. 23514 from 0003_limits.sql's size constraints) is
    // marked permanent so the controller drops the entries instead of
    // retry-looping on a payload that can never be accepted.
    const { error } = await this.client.from(table).upsert(rows, { onConflict: "id" });
    if (error) {
      const msg = `sync push (${table}) failed: ${error.message}`;
      throw isPermanentPushErrorCode(error.code) ? new PermanentSyncError(msg) : new Error(msg);
    }
  }

  async pull(table: string, since: number): Promise<SyncRecord[]> {
    const { data, error } = await this.client
      .from(table)
      .select("id, data, thumb_path, deleted, updated_at")
      .eq("owner_id", this.ownerId)
      .gte("updated_at", since);
    if (error) throw new Error(`sync pull (${table}) failed: ${error.message}`);

    const out: SyncRecord[] = [];
    for (const row of data as PulledRow[]) {
      if (row.deleted) {
        out.push({ id: row.id, updatedAt: row.updated_at, deleted: true, payload: null });
        continue;
      }
      const payload = { ...((row.data as Record<string, unknown>) ?? {}) };
      // Re-inline the thumbnail from Storage. If there's no path (legacy rows, or
      // rows whose upload fell back to inline) the thumbnail is already in `data`.
      if (row.thumb_path) {
        const dataUrl = await this.downloadThumb(row.thumb_path);
        if (dataUrl) payload.thumbnail = dataUrl;
        else console.warn(`[sync] thumbnail download failed for ${table}/${row.id}`);
      }
      out.push({ id: row.id, updatedAt: row.updated_at, deleted: false, payload });
    }
    return out;
  }
}
